/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 16/10/23
 */

import { createUnprovisionedContext } from '@/wpt-lib/utils/contextHelpers';
import React, { PropsWithChildren } from 'react';
import { joinPaths } from '@/utils/joinPaths';
import { useNavigate, useParams } from 'react-router-dom';

export interface INavigateProvider {
    prefix: string;
    supervisor: boolean;
    admin: boolean;
    mode?: 'create' | 'edit' | 'view';
    makePath: (...path: (string | number)[]) => string;
    makePathCustom: (options: {
        parent?: number;
        path: (string | number)[];
        params?: Record<string, string | number>;
        search?: string | URLSearchParams;
    }) => string;
    navigatePath: (...path: (string | number)[]) => void;
    navigatePathCustom: (params: {
        parent?: number;
        search: string | URLSearchParams;
        path: (string | number)[];
        params?: Record<string, string | number>;
    }) => void;
    workplanActions: boolean;
}

export const NavigateContext = createUnprovisionedContext<INavigateProvider>('NavigateContext');

type NavigateProps = PropsWithChildren &
    Partial<Omit<INavigateProvider, 'makePath' | 'navigatePath' | 'navigatePathCustom'>>;

const NavigateProvider: React.FC<NavigateProps> = ({
    children,
    prefix = '',
    supervisor = false,
    admin = false,
    mode,
    workplanActions = false,
}) => {
    const navigate = useNavigate();
    const urlParams = useParams();

    const makePathCustom: INavigateProvider['makePathCustom'] = ({ parent = 0, path, params = {}, search }) => {
        const prefixParts = prefix.split('/').map((p) => {
            if (p.charAt(0) === ':') {
                const param = p.substring(1);
                return params[param] ?? urlParams[param] ?? p;
            } else return p;
        });

        if (parent > 0) prefixParts.splice(prefixParts.length - 1);

        return joinPaths(...prefixParts, ...path) + (search ? `?${search.toString()}` : '');
    };

    const value: INavigateProvider = {
        prefix,
        supervisor,
        admin,
        mode,
        makePath: (...path: (string | number)[]) => makePathCustom({ path }),
        makePathCustom,
        navigatePath: (...path) => navigate(makePathCustom({ path })),
        navigatePathCustom: (options) => navigate(makePathCustom(options)),
        workplanActions,
    };

    return <NavigateContext.Provider value={value}>{children}</NavigateContext.Provider>;
};

export default NavigateProvider;
